import React from "react"
import { mergeDicts } from "@ui-seeker/utils"
import { Box, Grid } from "@mui/material"

import AppFilterPanel, { filterPanelGridSx } from "@components/@base/FilterPanel"
import AppStartHint from "@components/@base/StartHint"
import { dsColorNew } from "@palette"

import { isPreviousComparison, mainSiteProvider, showFeatures } from "@signals"
import {
	AcrissAutocomplete,
	AcrissCategoryToggleButton,
	AcrissGroupBySelect,
	AcrissGroupsAutocomplete,
	CategoriesAutocomplete,
	CurrencyAutocomplete,
	DailyPriceToggleButton,
	DateRangePicker,
	DirectSiteBrokerProviderToggleButton,
	DropoffOfficesAutocomplete,
	EndHourPicker,
	ExcludeProvidersSelect,
	ExcludeWebSuppliersSelect,
	FuelPolicyAutocomplete,
	GroupByAutocomplete,
	HowToGroupAutocomplete,
	InsuranceAutocomplete,
	InTerminalAutocomplete,
	LengthOfRentalAutocomplete,
	MainProvidersAutocomplete,
	MainSitesAutocomplete,
	MileageAutocomplete,
	NonAsyncOfficesAutocomplete,
	OfficesAutocomplete,
	OfficesPickoffAutocomplete,
	PeriodCompareToggleButton,
	PrepaidAutocomplete,
	PriceKpiToggleButton,
	ProvidersAutocomplete,
	RentalDatesMultiPicker,
	RentalLengthTags,
	ShowFeatures,
	SingleDatePicker,
	SitesAutocomplete,
	StartHourPicker,
	TiersAutocomplete,
	TransmissionAutocomplete,
	UsersAutocomplete,
	WebProviderToggleButton,
	WebSuppliersAutocomplete
} from "./@components"
import CommonFiltersFooterActions from "./FooterAction"
import { PeriodCompareInput } from "./@components/switch/ComparePeriod"
import { AppDatesAutocomplete } from "@components/Autocomplete"


export default function CommonFilters(
	{
		onSubmit,
		components = {},
		compProps = {},
		schema,
		resetFilters,
		isValid,
		...props
	}) {


	const { myComps, myProps } = {
		myComps: {
			priceKpiToggle: false,
			webProviderToggle: false,
			acrissCategoryToggle: false,
			directSiteBrokerProviderToggle: false,
			offices: false,
			officesPickoff: false,
			mainSite: false,
			sites: false,
			startHour: false,
			endHour: false,
			dateRange: false,
			dateBookingRange: false,
			datePresets: false,
			groupBy: false,
			howToGroup: false,
			rentalDates: false,
			rentalLength: false,
			lengthRental: false,
			categories: false,
			mainProvider: false,
			providers: false,
			tiers: false,
			singleDate: false,
			currency: false,
			showFeatures: false,
			acriss: false,
			mileage: false,
			transmission: false,
			fuelPolicy: false,
			insurance: false,
			prepaid: false,
			inTerminal: false,
			dropoffOffices: false,
			extraProviders: false,
			webSuppliers: false,
			extraCategories: false,
			dailyPrice: false,
			users: false,
			dates: false,
			analyzerAcriss: false,
			acrissGroups: false,
			acrissGroupBy: false,
			...components
		}, myProps: mergeDicts({
			offices: { grid: { xs: 3 } },
			officesPickoff: { grid: { xs: 3 } },
			mainSite: { grid: { xs: 3 } },
			sites: { grid: { xs: 3 } },
			startHour: { grid: { xs: 3 } },
			endHour: { grid: { xs: 3 } },
			rentalLength: { grid: { xs: 3 } },
			rentalDates: { grid: { xs: 3 } },
			dateRange: { grid: { xs: 3 } },
			groupBy: { grid: { xs: 1 } },
			howToGroup: { grid: { xs: 2 } },
			dateBookingRange: { grid: { xs: 3 } },
			isPreviousComparison: { grid: { xs: 2 } },
			lengthRental: { grid: { xs: 3 } },
			categories: { grid: { xs: 3 } },
			mainProvider: { grid: { xs: 3 } },
			providers: { grid: { xs: 3 } },
			tiers: { grid: { xs: 2 } },
			dates: { grid: { xs: 3 } },
			singleDate: { grid: { xs: 2 } },
			analyzerAcriss: { grid: { xs: 3 } },
			acrissGroups: { grid: { xs: 2 } },
			acrissGroupBy: { grid: { xs: 1.5 } },
			currency: { grid: { xs: 2 } },
			showFeatures: {},
			acriss: { grid: { xs: 3 }, sx: { display: showFeatures.value ? 'block' : 'none' } },
			mileage: { grid: { xs: 3 }, sx: { display: showFeatures.value ? 'block' : 'none' } },
			transmission: { grid: { xs: 3 }, sx: { display: showFeatures.value ? 'block' : 'none' } },
			fuelPolicy: { grid: { xs: 3 }, sx: { display: showFeatures.value ? 'block' : 'none' } },
			insurance: { grid: { xs: 2 }, sx: { display: showFeatures.value ? 'block' : 'none' } },
			prepaid: { grid: { xs: 2 }, sx: { display: showFeatures.value ? 'block' : 'none' } },
			inTerminal: { grid: { xs: 2 }, sx: { display: showFeatures.value ? 'block' : 'none' } },
			dropoffOffices: { grid: { xs: 2 } },
			extraProviders: { grid: { xs: 4 } },
			webSuppliers: { grid: { xs: 4 } },
			extraCategories: { grid: { xs: 3 } },
			dailyPrice: { grid: { xs: 1 } },
			users: { grid: { xs: 3 } },
		}, compProps)
	}


	// The Direct Sites / Broker toggle lives in this block too, so an analyzer that only
	// enables that one (one-way) must still open it.
	const showTogglesBlock = myComps.priceKpiToggle || myComps.webProviderToggle
		|| myComps.dailyPrice || myComps.directSiteBrokerProviderToggle
	// Nothing to filter yet (no report picked): Search can't be enabled either, so say what comes first.
	// The extra toggles (Category / Acriss, Main Direct Site / Provider…) only show inside the toggles block.
	// `hideExcludeWebSuppliers` is a flag on the web suppliers field, not a field of its own.
	const blockOnlyToggles = ['acrissCategoryToggle', 'directSiteBrokerProviderToggle', 'isPreviousComparison', 'hideExcludeWebSuppliers']
	const hasFields = Object.entries(myComps).some(([key, on]) => on && !blockOnlyToggles.includes(key))
	// the Direct Sites / Broker toggle alone is not something to filter: with nothing else the hint still shows
	const hasOwnToggles = myComps.priceKpiToggle || myComps.webProviderToggle || myComps.dailyPrice
	if (!hasFields && !hasOwnToggles) return <AppStartHint
		icon="file-chart-column"
		title="Select a report to start"
		description="Pick a report in the header, choose the office and click Search to see the prices"
		// the Virtual Tour lays its sample page here; the hint steps aside while it is on
		sx={{ 'body:has([data-tour-id="tour-price-cell"]) &': { display: 'none' } }}
	/>

	const hasFeatureFields = showFeatures.value && ['acriss', 'mileage', 'transmission', 'fuelPolicy', 'insurance', 'prepaid', 'inTerminal', 'extraProviders', 'webSuppliers', 'extraCategories'].some(key => myComps[key])

	return <AppFilterPanel
		toggles={showTogglesBlock && <>
			{myComps.priceKpiToggle && <PriceKpiToggleButton/>}
			{myComps.webProviderToggle && <WebProviderToggleButton/>}
			{myComps.acrissCategoryToggle && <AcrissCategoryToggleButton/>}
			{myComps.directSiteBrokerProviderToggle && <DirectSiteBrokerProviderToggleButton {...myProps.directSiteBrokerProviderToggle}/>}
			{myComps.dailyPrice && <DailyPriceToggleButton/>}
			{myComps.isPreviousComparison && <PeriodCompareToggleButton/>}
		</>}
		togglesRight={myComps.showFeatures && <ShowFeatures {...myProps.showFeatures} />}
		actions={<CommonFiltersFooterActions
			onSubmit={onSubmit}
			schema={schema}
			isValid={isValid}
			{...props} />}
	>
		<Box sx={filterPanelGridSx()}>

			{myComps.howToGroup && <Grid item {...myProps.howToGroup.grid}>
				<HowToGroupAutocomplete {...myProps.howToGroup} />
			</Grid>}

			{myComps.offices && <Grid item {...myProps.offices.grid}>
				{myProps.offices?.notAsync ? <NonAsyncOfficesAutocomplete {...myProps.offices} /> : <OfficesAutocomplete {...myProps.offices} />}
			</Grid>}

			{myComps.dropoffOffices && <Grid item {...myProps.dropoffOffices.grid}>
				<DropoffOfficesAutocomplete {...myProps.dropoffOffices} />
			</Grid>}

			{myComps.officesPickoff && <Grid item {...myProps.officesPickoff.grid}>
				<OfficesPickoffAutocomplete {...myProps.officesPickoff} />
			</Grid>}

			{myComps.mainSite && <Grid item {...myProps.mainSite.grid}>
				<MainSitesAutocomplete {...myProps.mainSite} />
			</Grid>}

			{myComps.mainProvider && (myComps.directSiteBrokerProviderToggle || mainSiteProvider.value) &&
				<Grid item {...myProps.mainProvider.grid}>
					<MainProvidersAutocomplete {...myProps.mainProvider} />
				</Grid>}

			{myComps.sites && <Grid item {...myProps.sites.grid}>
				<SitesAutocomplete {...myProps.sites} />
			</Grid>}

			{myComps.startHour && <Grid item {...myProps.startHour.grid}>
				<StartHourPicker {...myProps.startHour} />
			</Grid>}

			{myComps.endHour && <Grid item {...myProps.endHour.grid}>
				<EndHourPicker {...myProps.endHour} />
			</Grid>}

			{myComps.rentalDates && <Grid item {...myProps.rentalDates.grid}>
				<RentalDatesMultiPicker {...myProps.rentalDates} />
			</Grid>}

			{myComps.dateBookingRange && <Grid item {...myProps.dateBookingRange.grid}>
				<DateRangePicker {...myProps.dateBookingRange} />
			</Grid>}

			{myComps.dateRange && <Grid item {...myProps.dateRange.grid}>
				<DateRangePicker {...myProps.dateRange} />
			</Grid>}

			{myComps.groupBy && <Grid item {...myProps.groupBy.grid}>
				<GroupByAutocomplete {...myProps.groupBy} />
			</Grid>}

			{myComps.rentalLength && <Grid item {...myProps.rentalLength.grid}>
				<RentalLengthTags {...myProps.rentalLength} />
			</Grid>}

			{myComps.analyzerAcriss && <Grid item {...myProps.analyzerAcriss.grid} {...myProps.analyzerAcriss.sx}>
				<AcrissAutocomplete {...myProps.analyzerAcriss} />
			</Grid>}

			{myComps.acrissGroupBy && <Grid item {...myProps.acrissGroupBy.grid}>
				<AcrissGroupBySelect {...myProps.acrissGroupBy} />
			</Grid>}

			{myComps.acrissGroups && <Grid item {...myProps.acrissGroups.grid}>
				<AcrissGroupsAutocomplete {...myProps.acrissGroups} />
			</Grid>}

			{myComps.categories && <Grid item {...myProps.categories.grid}>
				<CategoriesAutocomplete {...myProps.categories} />
			</Grid>}

			{myComps.dates && <Grid item {...myProps.dates.grid}>
				<AppDatesAutocomplete {...myProps.dates} />
			</Grid>}

			{myComps.lengthRental && <Grid item {...myProps.lengthRental.grid}>
				<LengthOfRentalAutocomplete {...myProps.lengthRental} />
			</Grid>}

			{myComps.singleDate && <Grid item {...myProps.singleDate.grid}>
				<SingleDatePicker {...myProps.singleDate} />
			</Grid>}

			{myComps.tiers && <Grid item {...myProps.tiers.grid}>
				<TiersAutocomplete {...myProps.tiers} />
			</Grid>}

			{myComps.mainProvider && !myComps.directSiteBrokerProviderToggle && !mainSiteProvider.value &&
				<Grid item {...myProps.mainProvider.grid}>
					<MainProvidersAutocomplete {...myProps.mainProvider} />
				</Grid>}

			{myComps.providers && <Grid item {...myProps.providers.grid}>
				<ProvidersAutocomplete {...myProps.providers} />
			</Grid>}

			{myComps.currency && <Grid item {...myProps.currency.grid}>
				<CurrencyAutocomplete {...myProps.currency} />
			</Grid>}

			{myComps.isPreviousComparison && isPreviousComparison.value &&
				<Grid item {...myProps.isPreviousComparison.grid}>
					<PeriodCompareInput {...myProps.isPreviousComparison} />
				</Grid>}

			{myComps.users && <Grid item {...myProps.users.grid}>
				<UsersAutocomplete {...myProps.users} />
			</Grid>}

		</Box>

		{/* the feature filters mount only once "show features" is on: a mounted autocomplete fires its
		    request even when its grid item is display:none, and that was seven requests per page load */}
		<Box sx={{
			...filterPanelGridSx(),
			...hasFeatureFields && { mt: 2.25, pt: 2.25, borderTop: `1px dashed ${dsColorNew.grayBorder}` },
		}}>
			{myComps.acriss && showFeatures.value && <Grid item {...myProps.acriss.grid} {...myProps.acriss.sx}>
				<AcrissAutocomplete {...myProps.acriss} />
			</Grid>}

			{myComps.mileage && showFeatures.value && <Grid item {...myProps.mileage.grid} {...myProps.acriss.sx}>
				<MileageAutocomplete {...myProps.mileage} />
			</Grid>}

			{myComps.transmission && showFeatures.value && <Grid item {...myProps.transmission.grid} {...myProps.acriss.sx}>
				<TransmissionAutocomplete {...myProps.transmission} />
			</Grid>}

			{myComps.fuelPolicy && showFeatures.value && <Grid item {...myProps.fuelPolicy.grid} {...myProps.acriss.sx}>
				<FuelPolicyAutocomplete {...myProps.fuelPolicy} />
			</Grid>}

			{myComps.insurance && showFeatures.value && <Grid item {...myProps.insurance.grid} {...myProps.acriss.sx}>
				<InsuranceAutocomplete {...myProps.insurance} />
			</Grid>}

			{myComps.prepaid && showFeatures.value && <Grid item {...myProps.prepaid.grid} {...myProps.acriss.sx}>
				<PrepaidAutocomplete {...myProps.prepaid} />
			</Grid>}

			{myComps.inTerminal && showFeatures.value && <Grid item {...myProps.inTerminal.grid} {...myProps.acriss.sx}>
				<InTerminalAutocomplete {...myProps.inTerminal} />
			</Grid>}

			{myComps.extraProviders && showFeatures.value && <>
				<Grid item xs={2}>
					<ExcludeProvidersSelect/>
				</Grid>

				<Grid item {...myProps.extraProviders.grid}>
					<ProvidersAutocomplete {...myProps.extraProviders} />
				</Grid>
			</>}

			{myComps.webSuppliers && showFeatures.value && <>
				{!myComps.hideExcludeWebSuppliers && <Grid item xs={2}>
					<ExcludeWebSuppliersSelect/>
				</Grid>}

				<Grid item {...myProps.webSuppliers.grid}>
					<WebSuppliersAutocomplete {...myProps.webSuppliers} />
				</Grid>
			</>}

			{myComps.extraCategories && showFeatures.value && <Grid item {...myProps.extraCategories.grid}>
				<CategoriesAutocomplete {...myProps.extraCategories} />
			</Grid>}

		</Box>
	</AppFilterPanel>
}